import { usePermissionStore } from '../stores/permissionStore';
/**
 * Permission utilities for BarmetHub frontend
 * Maps user roles to SUPERADMIN/EMPLOYEE and checks module access.
 */

export type UserRole = 'owner' | 'admin' | 'manager' | 'member' | 'viewer';

/**
 * Is this user a SUPERADMIN?
 * Currently: role === 'owner'. Can be extended to check HubPermissionGrant.
 */
export function isSuperAdmin(role: UserRole | string | undefined): boolean {
  return role === 'owner' || role === 'OWNER';
}

/**
 * Permission keys for module access.
 * These map to HubPermission values in the backend.
 */
export type ModulePermission =
  | 'ADMINISTRATION_VIEW' | 'ADMINISTRATION_EDIT'
  | 'CRM_VIEW' | 'CRM_EDIT'
  | 'MARKETING_VIEW' | 'MARKETING_EDIT'
  | 'WEBS_VIEW' | 'WEBS_EDIT'
  | 'AUTOMATIONS_VIEW' | 'AUTOMATIONS_CREATE' | 'AUTOMATIONS_EXECUTE'
  | 'AI_USE'
  | 'NEXUS_USE' | 'NEXUS_ADMIN'
  | 'MAIL_READ_OWN' | 'MAIL_SEND_OWN' | 'MAIL_LINK_PROJECT'
  | 'HERMES_VIEW' | 'HERMES_USE' | 'HERMES_EXECUTE' | 'HERMES_ADMIN'
  | 'AGENTS_VIEW' | 'AGENTS_MANAGE'
  | 'SKILLS_VIEW' | 'SKILLS_MANAGE'
  | 'MCP_VIEW' | 'MCP_MANAGE'
  | 'TOOLS_USE'
  | 'SYSTEM_SETTINGS'
  | 'USER_MANAGEMENT'
  | 'SECURITY_ADMIN_PERM'
  | 'ACTIVITY_LOG_VIEW'
  | 'SETUP_CENTER_ACCESS'
  | 'PROJECT_VIEW' | 'PROJECT_MANAGE'
  | 'PROJECTS_VIEW' | 'PROJECTS_EDIT' | 'PROJECTS_ADMIN'
  | 'TASKS_VIEW' | 'TASKS_CREATE' | 'TASKS_EDIT' | 'TASKS_ASSIGN' | 'TASKS_ADMIN'
  | 'TIME_VIEW_OWN' | 'TIME_LOG' | 'TIME_EDIT_OWN' | 'TIME_REVIEW' | 'TIME_APPROVE' | 'TIME_EXPORT'
  | 'INTEGRATIONS_VIEW' | 'INTEGRATIONS_MANAGE'
  | 'DASHBOARD_VIEW'
  | 'EMPLOYEES_VIEW' | 'EMPLOYEES_IMPORT' | 'EMPLOYEES_MANAGE'
  | 'FACTORY_PROMPTS_VIEW' | 'FACTORY_PROMPTS_USE' | 'FACTORY_PROMPTS_EDIT'
  | 'FACTORY_PROMPTS_SHARE' | 'FACTORY_PROMPTS_ADMIN'
  | 'FACTORY_PROMPTS_FACTORY_RUN' | 'FACTORY_PROMPTS_TRACE_VIEW'
  | 'LASSPASS_VIEW' | 'LASSPASS_USE' | 'LASSPASS_EDIT' | 'LASSPASS_SHARE' | 'LASSPASS_ADMIN';

/**
 * Default permissions by role.
 * SUPERADMIN (owner) gets everything.
 * Others get subsets based on their role.
 */
const DEFAULT_PERMISSIONS: Record<string, ModulePermission[]> = {
  owner: [
    // SUPERADMIN gets ALL permissions
    'ADMINISTRATION_VIEW', 'ADMINISTRATION_EDIT',
    'CRM_VIEW', 'CRM_EDIT',
    'MARKETING_VIEW', 'MARKETING_EDIT',
    'WEBS_VIEW', 'WEBS_EDIT',
    'AUTOMATIONS_VIEW', 'AUTOMATIONS_CREATE', 'AUTOMATIONS_EXECUTE',
    'AI_USE',
    'HERMES_VIEW', 'HERMES_USE', 'HERMES_EXECUTE', 'HERMES_ADMIN',
    'AGENTS_VIEW', 'AGENTS_MANAGE',
    'SKILLS_VIEW', 'SKILLS_MANAGE',
    'MCP_VIEW', 'MCP_MANAGE',
    'TOOLS_USE',
    'SYSTEM_SETTINGS',
    'USER_MANAGEMENT',
    'SECURITY_ADMIN_PERM',
    'ACTIVITY_LOG_VIEW',
    'SETUP_CENTER_ACCESS',
    'PROJECT_VIEW', 'PROJECT_MANAGE',
    'INTEGRATIONS_VIEW', 'INTEGRATIONS_MANAGE',
    'DASHBOARD_VIEW',
    'EMPLOYEES_VIEW', 'EMPLOYEES_IMPORT', 'EMPLOYEES_MANAGE',
    'FACTORY_PROMPTS_VIEW', 'FACTORY_PROMPTS_USE', 'FACTORY_PROMPTS_EDIT',
    'FACTORY_PROMPTS_SHARE', 'FACTORY_PROMPTS_ADMIN',
    'FACTORY_PROMPTS_FACTORY_RUN', 'FACTORY_PROMPTS_TRACE_VIEW',
  ],
  admin: [
    'ADMINISTRATION_VIEW', 'ADMINISTRATION_EDIT',
    'CRM_VIEW', 'CRM_EDIT',
    'MARKETING_VIEW', 'MARKETING_EDIT',
    'WEBS_VIEW', 'WEBS_EDIT',
    'AUTOMATIONS_VIEW', 'AUTOMATIONS_CREATE',
    'AI_USE',
    'HERMES_VIEW', 'HERMES_USE',
    'AGENTS_VIEW',
    'SKILLS_VIEW',
    'MCP_VIEW',
    'TOOLS_USE',
    'ACTIVITY_LOG_VIEW',
    'PROJECT_VIEW', 'PROJECT_MANAGE',
    'INTEGRATIONS_VIEW', 'INTEGRATIONS_MANAGE',
    'DASHBOARD_VIEW',
  ],
  manager: [
    'CRM_VIEW', 'CRM_EDIT',
    'MARKETING_VIEW', 'MARKETING_EDIT',
    'WEBS_VIEW',
    'AUTOMATIONS_VIEW',
    'AI_USE',
    'HERMES_VIEW',
    'AGENTS_VIEW',
    'PROJECT_VIEW',
    'DASHBOARD_VIEW',
  ],
  member: [
    'CRM_VIEW',
    'MARKETING_VIEW',
    'WEBS_VIEW',
    'AI_USE',
    'HERMES_VIEW',
    'PROJECT_VIEW',
    'DASHBOARD_VIEW',
  ],
  viewer: [
    'DASHBOARD_VIEW',
    'PROJECT_VIEW',
  ],
};

/**
 * Check if a user role has a specific permission.
 * Uses default permissions. In future, will also check HubPermissionGrant overrides.
 */
export function hasPermission(role: UserRole | string, permission: ModulePermission): boolean {
  if (isSuperAdmin(role)) return true;
  // Employees: only explicit grants of READY modules, resolved by the API (no role defaults).
  return usePermissionStore.getState().permissions.includes(permission);
}

/**
 * Get all permissions for a role.
 */
export function getPermissionsForRole(role: UserRole | string): ModulePermission[] {
  if (isSuperAdmin(role)) return DEFAULT_PERMISSIONS.owner;
  return DEFAULT_PERMISSIONS[role] || DEFAULT_PERMISSIONS.viewer;
}

/**
 * Navigation item with permission requirement.
 */
export interface NavItem {
  to: string;
  icon: string;
  label: string;
  /** Permission required to see this item. null = visible to all. */
  permission: ModulePermission | null;
  /** If true, only SUPERADMIN sees this. */
  superAdminOnly?: boolean;
}

/**
 * Filter navigation items by user role.
 */
export function filterNav(items: NavItem[], role: UserRole | string): NavItem[] {
  return items.filter(item => {
    if (item.superAdminOnly && !isSuperAdmin(role)) return false;
    if (item.permission && !hasPermission(role, item.permission)) return false;
    return true;
  });
}